import { motion } from "framer-motion";
import { CheckCircle2, Mail, Clock, ShieldCheck, Info } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useBranding } from "@/hooks/useCms";

/**
 * Modal de confirmação após submissão — layout ultra-compacto INXPEK.
 */
export default function SuccessModal({
  open,
  onClose,
  title,
  name = "",
  email = "",
  details = [],
  highlight = null,
  message = "",
  eyebrow = "",
  secondaryAction = null,
  assurance = "",
  secondaryTagline = "",
  onCloseAction = null,
  closeLabel = "Concluído",
  originNotice = null,
  testId = "success-modal",
}) {
  const firstName = (name || "").trim().split(/\s+/)[0] || "";
  const { logoDark } = useBranding();
  const hasHighlight = !!highlight;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        data-testid={testId}
        className="p-0 gap-0 overflow-hidden border-0 shadow-2xl w-[calc(100vw-1.5rem)] max-w-[480px] sm:max-w-[480px] max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100vh-2rem)] rounded-2xl bg-white dark:bg-slate-900 !flex !flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          {`Confirmação de submissão para ${name || "o utilizador"}. Uma resposta será enviada para ${email}.`}
        </DialogDescription>

        {/* Wrapper com scroll interno */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-visible">

        {/* Cabeçalho compacto */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-[#0F7484] px-5 pt-2.5 pb-6 text-white text-center">
          <div className="absolute inset-0 opacity-40 pointer-events-none">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#1698AC]/40 blur-3xl" />
            <div className="absolute -bottom-16 -left-10 w-48 h-48 rounded-full bg-[#1698AC]/25 blur-3xl" />
          </div>
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.9) 1px, transparent 0)",
              backgroundSize: "18px 18px",
            }}
          />

          {logoDark && (
            <img
              src={logoDark}
              alt="INXPEK"
              data-testid={`${testId}-logo`}
              className="relative mx-auto h-5 w-auto object-contain mb-2 drop-shadow-sm"
            />
          )}

          {eyebrow && (
            <p className="relative text-[9px] tracking-[0.28em] uppercase font-semibold text-white/80 mb-1.5">
              {eyebrow}
            </p>
          )}

          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.1 }}
            className="relative mx-auto w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center ring-[3px] ring-white/25 mb-1.5"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.35 }}
            >
              <CheckCircle2 size={22} strokeWidth={2.2} className="text-white drop-shadow" />
            </motion.div>
          </motion.div>

          <motion.h2
            initial={{ y: 6, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="relative font-heading text-lg leading-tight tracking-tight"
          >
            {title}
          </motion.h2>
          {firstName && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="relative mt-0.5 text-xs text-white/85"
            >
              Obrigado, <span className="font-semibold text-white">{firstName}</span>.
            </motion.p>
          )}
        </div>

        {/* Corpo */}
        <div className="relative z-10 px-5 pt-0 pb-3 -mt-5">
          {highlight && (
            <motion.div
              initial={{ y: 10, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 200, damping: 20 }}
              data-testid={`${testId}-highlight`}
              className="rounded-xl bg-gradient-to-br from-[#1698AC] to-[#0F7484] text-white shadow-lg ring-4 ring-white/60 dark:ring-white/10 px-4 py-2 mb-2.5 text-center"
            >
              <p className="text-[9px] tracking-[0.24em] uppercase font-semibold text-white/80">
                {highlight.label}
              </p>
              <p className="font-heading text-xl sm:text-2xl font-semibold mt-0.5 leading-none drop-shadow-sm">
                {highlight.value}
              </p>
              {highlight.note && (
                <p className="text-[10px] text-white/85 mt-1 leading-snug">{highlight.note}</p>
              )}
            </motion.div>
          )}

          {originNotice && (
            <motion.div
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.35 }}
              data-testid={`${testId}-origin-notice`}
              className="flex items-start gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-400/20 px-3 py-2 mb-2.5"
            >
              <Info size={14} className="text-emerald-600 dark:text-emerald-300 shrink-0 mt-[1px]" />
              <p className="text-[12px] leading-snug text-emerald-800 dark:text-emerald-100">
                {originNotice}
              </p>
            </motion.div>
          )}

          {details && details.length > 0 && (
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10 shadow-lg divide-y divide-slate-100 dark:divide-white/5 mb-2.5"
            >
              {details.filter((d) => d && (d.value || d.value === 0)).map((d, i) => {
                const Icon = d.icon;
                const isMulti = !!d.multiline;
                return (
                  <div key={i} className="flex items-start gap-2 px-3 py-[5px]">
                    {Icon && (
                      <div className="w-5 h-5 shrink-0 rounded bg-[#1698AC]/10 text-[#1698AC] dark:text-[#4FC6D8] flex items-center justify-center mt-[1px]">
                        <Icon size={11} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[8px] tracking-[0.14em] uppercase font-semibold text-slate-400 dark:text-slate-500 leading-none">
                        {d.label}
                      </p>
                      <p
                        className={
                          "text-xs font-medium text-slate-800 dark:text-slate-100 leading-snug " +
                          (isMulti
                            ? "whitespace-pre-wrap break-words"
                            : "truncate")
                        }
                      >
                        {d.value}
                      </p>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}

          {/* Reforço de confiança — linha única compacta */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400"
          >
            {email && (
              <span className="inline-flex items-center gap-1">
                <Mail size={11} className="text-[#1698AC] shrink-0" />
                Confirmação para <span className="font-semibold text-slate-700 dark:text-slate-200">{email}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock size={11} className="text-[#1698AC] shrink-0" />
              {assurance || <>Resposta em <span className="font-semibold text-slate-700 dark:text-slate-200">24h úteis</span></>}
            </span>
            <span className="inline-flex items-center gap-1">
              <ShieldCheck size={11} className="text-[#1698AC] shrink-0" />
              RGPD
            </span>
          </motion.div>

          {message && (
            <p className="text-[10px] text-slate-400 dark:text-slate-400 leading-snug mt-1.5">
              {message}
            </p>
          )}
        </div>
        </div>{/* fim do wrapper scrollável */}

        {/* Rodapé fixo */}
        <div className="shrink-0 px-5 py-2 border-t border-slate-100 dark:border-white/10 bg-white dark:bg-slate-900 space-y-1.5">
          {secondaryAction && secondaryTagline && (
            <p className="text-center text-[11px] text-slate-500 dark:text-slate-400 leading-snug px-1">
              {secondaryTagline}
            </p>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              data-testid={`${testId}-secondary`}
              className="w-full h-9 rounded-full bg-[#1698AC] hover:bg-[#0F7484] text-white text-sm font-semibold transition-colors shadow-md hover:shadow-lg active:scale-[0.98] inline-flex items-center justify-center gap-2"
            >
              {secondaryAction.icon && (
                <secondaryAction.icon size={14} strokeWidth={2.2} />
              )}
              <span>{secondaryAction.label}</span>
            </button>
          )}
          <button
            type="button"
            onClick={onCloseAction || onClose}
            data-testid={`${testId}-close`}
            className={`w-full h-9 rounded-full text-sm font-semibold transition-colors active:scale-[0.98] ${
              secondaryAction
                ? "border-2 border-slate-200 dark:border-white/15 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"
                : "bg-[#1698AC] hover:bg-[#0F7484] text-white shadow-md hover:shadow-lg"
            }`}
          >
            {closeLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
